import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

export default function Navigation() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#work", label: t.nav.work, id: "work" },
    { href: "#services", label: t.nav.services, id: "services" },
    { href: "#manifesto", label: t.nav.manifesto, id: "manifesto" },
    { href: "#team", label: t.nav.team, id: "team" },
    { href: "#contact", label: t.nav.contact, id: "contact" },
  ];

  const navigateToAnchor = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (onHome) {
      const target = document.querySelector(`#${id}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      navigate(`/#${id}`);
      // After route change, scroll to anchor
      setTimeout(() => {
        const target = document.querySelector(`#${id}`);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "backdrop-blur-xl bg-[#050505]/60 border-b hairline" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <nav className="flex items-center justify-between px-6 md:px-12 py-5">
          <Link
            to="/"
            className="font-display font-extrabold text-lg tracking-tight uppercase"
            data-testid="nav-logo"
          >
            HK<span className="text-[color:var(--accent)]">.</span>Brand Lab
          </Link>

          <ul className="hidden md:flex items-center gap-10">
            {links.map((l) => (
              <li key={l.id}>
                <a
                  href={onHome ? l.href : `/${l.href}`}
                  onClick={navigateToAnchor(l.id)}
                  className="text-xs uppercase tracking-[0.22em] link-hover"
                  data-testid={`nav-link-${l.id}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4">
            <div
              className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.22em]"
              role="group"
              aria-label="Language switcher"
            >
              <button
                onClick={() => setLang("en")}
                className={`transition-colors ${lang === "en" ? "text-[color:var(--fg)]" : "text-[color:var(--muted)]"}`}
                data-testid="language-switcher-en"
              >
                EN
              </button>
              <span className="text-[color:var(--muted)]">/</span>
              <button
                onClick={() => setLang("it")}
                className={`transition-colors ${lang === "it" ? "text-[color:var(--fg)]" : "text-[color:var(--muted)]"}`}
                data-testid="language-switcher-it"
              >
                IT
              </button>
            </div>

            <a
              href={onHome ? "#contact" : "/#contact"}
              onClick={navigateToAnchor("contact")}
              className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] border border-[color:var(--fg)] rounded-full px-5 py-3 hover:bg-[color:var(--accent)] hover:border-[color:var(--accent)] hover:text-[#050505] transition-colors"
              data-testid="nav-cta"
            >
              {t.nav.cta}
              <span aria-hidden>→</span>
            </a>

            <button
              className="md:hidden inline-flex flex-col gap-1.5 p-2"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Toggle menu"
              data-testid="nav-mobile-toggle"
            >
              <span className={`block h-px w-6 bg-[color:var(--fg)] transition-transform ${mobileOpen ? "translate-y-[6px] rotate-45" : ""}`} />
              <span className={`block h-px w-6 bg-[color:var(--fg)] transition-opacity ${mobileOpen ? "opacity-0" : ""}`} />
              <span className={`block h-px w-6 bg-[color:var(--fg)] transition-transform ${mobileOpen ? "-translate-y-[6px] -rotate-45" : ""}`} />
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 bg-[#050505] md:hidden pt-24 px-6"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                >
                  <a
                    href={onHome ? l.href : `/${l.href}`}
                    onClick={navigateToAnchor(l.id)}
                    className="font-display text-4xl uppercase tracking-tight"
                    data-testid={`mobile-nav-link-${l.id}`}
                  >
                    {l.label}
                  </a>
                </motion.li>
              ))}
            </ul>
            <div className="mt-10 flex gap-4 text-sm uppercase tracking-[0.22em]">
              <button
                onClick={() => setLang("en")}
                className={lang === "en" ? "text-[color:var(--fg)]" : "text-[color:var(--muted)]"}
                data-testid="mobile-lang-en"
              >
                EN
              </button>
              <span className="text-[color:var(--muted)]">/</span>
              <button
                onClick={() => setLang("it")}
                className={lang === "it" ? "text-[color:var(--fg)]" : "text-[color:var(--muted)]"}
                data-testid="mobile-lang-it"
              >
                IT
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
